import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { TrayArrowDown, FileText, Plus, ArrowRight, MagicWand, Envelope, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";

const KAT_LABEL = {
  gutachten: "Gutachten",
  bussgeld: "Bußgeldbescheid",
  sonstige: "Sonstiges",
};

export default function Eingang() {
  const [data, setData] = useState({ unzugeordnete_dokumente: [], akten_im_eingang: [] });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [imapSyncing, setImapSyncing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/eingang");
      setData(r.data);
    } catch {
      toast.error("Konnte Eingang nicht laden");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const syncImap = async () => {
    setImapSyncing(true);
    try {
      const r = await api.post("/imap/sync");
      const n = r.data.documents_imported;
      if (n > 0) toast.success(`${n} neues Dokument aus dem Postfach importiert`);
      else toast.info("Keine neuen Dokumente");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (detail.includes("nicht konfiguriert")) {
        toast.error("Bitte zuerst IMAP in Einstellungen → E-Mail konfigurieren");
      } else {
        toast.error(`IMAP-Fehler: ${detail || "Verbindung fehlgeschlagen"}`);
      }
    } finally { setImapSyncing(false); }
  };

  const handleUpload = async (files) => {
    if (!files || !files.length) return;
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kategorie", "gutachten");
      try {
        await api.post("/dokumente/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`${f.name} hochgeladen`);
      } catch {
        toast.error(`Fehler bei ${f.name}`);
      }
    }
    load();
  };

  const aktenZuDok = async (dok) => {
    setBusyId(dok.id);
    try {
      const r = await api.post("/akten", { mandant_name: "Neuer Mandant", art: dok.kategorie === "bussgeld" ? "owi" : "unfall" });
      const akteId = r.data.id;
      await api.patch(`/dokumente/${dok.id}`, { akte_id: akteId });
      toast.success("Akte angelegt — bitte Mandant ergänzen");
      window.location.href = `/app/akten/${akteId}`;
    } catch {
      toast.error("Fehler beim Anlegen");
    } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-8" data-testid="eingang-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline text-[#D32F2F] mb-2">Workflow · 1</p>
          <h1 className="font-serif text-3xl lg:text-4xl">Eingang</h1>
          <p className="text-sm text-[#6B7280] mt-2 max-w-xl">
            Neue Dokumente landen hier — per Drag &amp; Drop oder Datei-Upload. Aus jedem Dokument können Sie mit einem Klick eine Akte starten.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={syncImap} disabled={imapSyncing} data-testid="eingang-imap-sync-btn">
            {imapSyncing ? <Spinner size={14} weight="bold" className="mr-2 animate-spin" /> : <Envelope size={16} className="mr-2" weight="duotone" />}
            E-Mails abrufen
          </Button>
          <Button asChild className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="eingang-new-akte-btn">
            <Link to="/app/akten"><Plus size={16} className="mr-2" /> Akte anlegen</Link>
          </Button>
        </div>
      </header>

      {/* Drop-Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-[#1A2238]", "bg-[#F3F4F6]"); }}
        onDragLeave={(e) => e.currentTarget.classList.remove("border-[#1A2238]", "bg-[#F3F4F6]")}
        onDrop={(e) => {
          e.preventDefault();
          e.currentTarget.classList.remove("border-[#1A2238]", "bg-[#F3F4F6]");
          handleUpload(Array.from(e.dataTransfer.files || []));
        }}
        className="border-2 border-dashed border-[#E5E7EB] rounded-xl p-10 text-center transition-colors"
        data-testid="eingang-drop-zone"
      >
        <TrayArrowDown size={40} weight="duotone" className="text-[#1A2238] mx-auto mb-3" />
        <p className="font-serif text-xl mb-1">Dokument hier ablegen</p>
        <p className="text-sm text-[#6B7280] mb-4">PDF · JPG · PNG — wird automatisch klassifiziert (Gutachten / OWi)</p>
        <label className="inline-block">
          <input type="file" multiple accept="application/pdf,image/*" className="hidden" onChange={(e) => handleUpload(Array.from(e.target.files || []))} data-testid="eingang-file-input" />
          <span className="px-4 py-2 bg-[#1A2238] text-white rounded-md text-sm cursor-pointer hover:bg-[#2C3859]">Datei auswählen</span>
        </label>
      </div>

      {/* Unzugeordnete Dokumente */}
      <section data-testid="eingang-unzugeordnet-section">
        <h2 className="font-serif text-2xl mb-4">Unzugeordnete Dokumente <span className="text-[#9CA3AF] text-base font-sans">({data.unzugeordnete_dokumente.length})</span></h2>
        {loading ? (
          <p className="text-sm text-[#9CA3AF]">Lade …</p>
        ) : data.unzugeordnete_dokumente.length === 0 ? (
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 text-center text-sm text-[#6B7280]">
            Keine offenen Dokumente. Alles eingeordnet.
          </div>
        ) : (
          <ul className="space-y-2">
            {data.unzugeordnete_dokumente.map(d => (
              <li key={d.id} className="bg-white border border-[#E5E7EB] rounded-lg p-4 flex items-center gap-4">
                <FileText size={28} weight="duotone" className="text-[#1A2238] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{d.original_filename}</p>
                  <p className="text-xs text-[#9CA3AF]">
                    {KAT_LABEL[d.kategorie] || d.kategorie} · {(d.size / 1024).toFixed(0)} KB · {new Date(d.created_at).toLocaleString("de-DE")}
                  </p>
                </div>
                <Button onClick={() => aktenZuDok(d)} disabled={busyId === d.id} size="sm" data-testid={`eingang-create-akte-${d.id}`}>
                  <MagicWand size={14} className="mr-1" /> Akte starten
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Akten im Eingang */}
      <section data-testid="eingang-akten-section">
        <h2 className="font-serif text-2xl mb-4">Akten im Eingang <span className="text-[#9CA3AF] text-base font-sans">({data.akten_im_eingang.length})</span></h2>
        {data.akten_im_eingang.length === 0 ? (
          <p className="text-sm text-[#9CA3AF]">Keine neuen Akten warten auf Bearbeitung.</p>
        ) : (
          <ul className="space-y-2">
            {data.akten_im_eingang.map(a => (
              <li key={a.id} className="bg-white border border-[#E5E7EB] rounded-lg p-4 flex items-center justify-between gap-4">
                <div>
                  <p className="font-medium">{a.aktenzeichen} · {a.mandant_name || "—"}</p>
                  <p className="text-xs text-[#9CA3AF]">{a.art === "owi" ? "OWi" : "Unfall"} · {new Date(a.created_at).toLocaleDateString("de-DE")}</p>
                </div>
                <Button asChild variant="outline" size="sm">
                  <Link to={`/app/akten/${a.id}`}>Öffnen <ArrowRight size={14} className="ml-1" /></Link>
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
